import { combineReducers, AnyAction } from 'redux';
import { connectRouter } from 'connected-react-router';
import { History } from 'history';
import { reducer as formReducer } from 'redux-form';
import { localizeReducer as localize } from 'react-localize-redux';
import { responsiveStateReducer } from 'redux-responsive';
import { reducer as notifications } from 'react-notification-system-redux';
import { loadingBarReducer } from 'react-redux-loading-bar';

import administratorRoles from './administratorRoles';
import administrators from './administrators';
import availableResources from './availableResources';
import availableTimes from './availableTimes';
import billing from './billing';
import billmate from './billmate';
import booking from './newBooking';
import bookingDetails from './bookingDetails';
import bookingLogs from './bookingLogs';
import bookingReportOptions from './bookingReportOptions';
import bookings from './bookings';
import bookingSettings from './bookingSettings';
import bookingStatuses from './bookingStatuses';
import bookingUserQueue from './bookingUserQueue';
import collidingEvents from './collidingEvents';
import company from './company';
import companyCategories from './companyCategories';
import companyTypes from './companyTypes';
import confirm from './confirm';
import coordinates from './coordinates';
import countries from './countries';
import currencies from './currencies';
import customerComments from './customerComments';
import customers from './customers';
import customFields from './customFields';
import customFieldSlots from './customFieldSlots';
import gdpr from './gdpr';
import homepage from './homepage';
import images from './images';
import licenses from './licenses';
import messages from './messages';
import newsletters from './newsletters';
import paymentSettings from './paymentSettings';
import prices from './prices';
import promoCodes from './promoCodes';
import bookingPrintout from './bookingPrintout';
import resources from './resources';
import resourceTypes from './resourcetypes';
import schedules from './schedules';
import schedulePreview from './schedulePreview';
import scheduleTypes from './scheduleTypes';
import serviceGroups from './serviceGroups';
import services from './services';
import settings from './settings';
import statistics from './statistics';
import supportTickets from './supportTickets';
import sync from './sync';
import timeExceptions from './timeExceptions';
import validations from './validations';
import verify from './verify';
import codelockSettings from './codelockSettings';
import axemaSettings from './axemaSettings';
import vanderbiltSettings from './vanderbiltSettings';
import parakeySettings from './parakeySettings';
import amidoSettings from './amidoSettings';
import telkeySettings from './telkeySettings';
import siedleSettings from './siedleSettings';
import rcoSettings from './rcoSettings';
import aptusSettings from './aptusSettings';
import accessKeyTypes from './accessKeyTypes';
import agreements from './agreements';
import accessySettings from './accessySettings';
import priceViewTypes from './priceViewTypes';
import zesecSettings from './zesecSettings';
import iloqSettings from './iloqSettings';
import newAuth from './newAuth';

import bmApiService from '@/services/bmApi';
import cancelLicenseQuestionaire from './cancelLicenseQuestionaire';
import resyncCodelock from './resyncCodelock';
import serviceDurationTypes from './servicesDurationTypes';
import calculationTypes from './calculationTypes';
import eaccountingSlice from './eaccounting';
import correlationId from './correlationId';
import calculatePrice from './calculatePrice';
import dateQuantitiesForm from './dateQuantitiesForm';
import promoCodesTransactions from './promoCodesTransactions'
import stripeAuth from './stripeAuthSlice';
import mailchimpSettings from './mailchimpSettings';
import webhookSettings from './webhookSettings';
import webhookEndpoints from './webhookEndpoints';
import modalSlice from './modal';
import incentivesSlice from './incentives';
import sequentialModal from './sequentialModal';
import { similarServiceSlice } from './similarServicesSlice';
import mailchimpSyncLog from './mailchimpSyncLog';
import articleTypes from './articleTypes';
import payments from './payments';
import testWebhook from './webhookTest';
import errorCheck from './errorCheck';


const createAppReducer = (history: History) =>
    // @ts-ignore
    combineReducers({
        router: connectRouter(history),
        form: formReducer,
        browser: responsiveStateReducer,
        notifications,
        loadingBar: loadingBarReducer,
        localize,
        cancelLicenseQuestionaire: cancelLicenseQuestionaire.reducer,
        administratorRoles,
        administrators,
        authenticate: newAuth.reducer,
        resyncCodelock: resyncCodelock.reducer,
        availableResources,
        availableTimes,
        billing,
        billmate,
        booking,
        bookingDetails,
        bookingLogs,
        bookingReportOptions,
        bookings,
        bookingUserQueue,
        bookingSettings,
        bookingStatuses,
        collidingEvents,
        company,
        companyCategories,
        companyTypes,
        confirm,
        coordinates,
        countries,
        currencies,
        customerComments,
        customers,
        customFields,
        customFieldSlots,
        gdpr,
        homepage,
        images,
        licenses,
        messages,
        newsletters,
        paymentSettings,
        prices,
        promoCodes,
        bookingPrintout,
        resources,
        resourceTypes,
        schedules,
        schedulePreview,
        scheduleTypes,
        serviceGroups,
        services,
        settings,
        statistics,
        supportTickets,
        sync,
        timeExceptions,
        validations,
        verify,
        codelockSettings,
        axemaSettings,
        vanderbiltSettings,
        parakeySettings,
        amidoSettings,
        telkeySettings,
        siedleSettings,
        rcoSettings,
        aptusSettings,
        accessySettings,
        accessKeyTypes,
        agreements,
        priceViewTypes,
        zesecSettings,
        iloqSettings,
        serviceDurationTypes,
        calculationTypes,
        eaccounting: eaccountingSlice.reducer,
        promoCodesTransactions,
        mailchimpSettings,
        webhookSettings,
        webhookEndpoints,
        [sequentialModal.name]: sequentialModal.reducer,
        testWebhook,
        errorCheck,
        mailchimpSyncLog,
        articleTypes,
        payments,
        [correlationId.name]: correlationId.reducer,
        [calculatePrice.name]: calculatePrice.reducer,
        [dateQuantitiesForm.name]: dateQuantitiesForm.reducer,
        [stripeAuth.name]: stripeAuth.reducer,
        [modalSlice.name]: modalSlice.reducer,
        [incentivesSlice.name]: incentivesSlice.reducer,
        [similarServiceSlice.name]: similarServiceSlice.reducer,
        [bmApiService.reducerPath]: bmApiService.reducer
    });


const createRootReducer = (history: History) => (state: any, action: AnyAction) => {
    if (action.type === 'RESET_STORE') {
        const { browser, localize, router } = state;
        state = { browser, localize, router };
    }
    return createAppReducer(history)(state, action);
};

export default createRootReducer;
